import { useState } from 'react';
import {
  Box,
  ButtonBase,
  Popover,
  List,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Typography,
  Divider,
  TextField,
  InputAdornment,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import SettingsIcon from '@mui/icons-material/Settings';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import SearchIcon from '@mui/icons-material/Search';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { resolveLimits } from '@traaker/shared';
import { useBrand } from '../../contexts/BrandContext';
import { useAuth } from '../../contexts/AuthContext';
import { BrandFavicon } from '../brand/BrandFavicon';

export default function BrandSelector() {
  const { brands, activeBrand, setActiveBrand } = useBrand();
  const { tenant } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [search, setSearch] = useState('');

  if (!activeBrand) return null;

  const tier = tenant?.tier || 'basic';
  // "Multi-brand" = tier that allows more than one brand (drives the picker
  // affordance). Derive from the resolved limit so it stays correct as tier
  // numbers change; -1 = unlimited.
  const brandLimit = resolveLimits(tier, tenant?.settings?.quotaOverrides).brands;
  const isMultiBrand = brandLimit === -1 || brandLimit > 1;
  // Surface the search box once the list grows past 8 brands — agency tenants
  // can have 100+ and the tier check alone isn't enough.
  const showSearch = tier === 'enterprise' || brands.length > 8;

  const filtered = brands.filter((b) =>
    (b.name || '').toLowerCase().includes(search.toLowerCase()),
  );

  return (
    <>
      <ButtonBase
        data-tour="brand-selector"
        onClick={isMultiBrand ? (e) => setAnchorEl(e.currentTarget) : undefined}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.75,
          px: 1.5,
          py: 0.5,
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
          cursor: isMultiBrand ? 'pointer' : 'default',
          '&:hover': isMultiBrand ? { bgcolor: 'action.hover' } : {},
          maxWidth: 220,
        }}
      >
        <BrandFavicon brandId={activeBrand.brandId} size={20} />
        <Typography
          variant="body2"
          fontWeight={500}
          noWrap
          sx={{ color: 'text.primary' }}
        >
          {activeBrand.name}
        </Typography>
        {isMultiBrand && (
          <UnfoldMoreIcon sx={{ fontSize: 16, color: 'text.secondary', ml: 0.25 }} />
        )}
      </ButtonBase>

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => { setAnchorEl(null); setSearch(''); }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{ paper: { sx: { width: 260, mt: 0.5 } } }}
      >
        <Box sx={{ px: 2, pt: 1.5, pb: 0.5 }}>
          <Typography variant="caption" color="text.secondary" fontWeight={600} textTransform="uppercase">
            {t('brandSelector.brands', { count: brands.length })}
          </Typography>
        </Box>

        {showSearch && (
          <Box sx={{ px: 1.5, pb: 0.5 }}>
            <TextField
              size="small"
              placeholder={t('brandSelector.searchPlaceholder')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              fullWidth
              autoFocus
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ fontSize: 18 }} />
                    </InputAdornment>
                  ),
                },
              }}
            />
          </Box>
        )}

        <List dense sx={{ maxHeight: 240, overflow: 'auto' }}>
          {filtered.map((brand) => (
            <ListItemButton
              key={brand.brandId}
              selected={brand.brandId === activeBrand.brandId}
              onClick={() => {
                setActiveBrand(brand);
                setAnchorEl(null);
                setSearch('');
              }}
              sx={{ borderRadius: 1, mx: 0.5, gap: 1 }}
            >
              <BrandFavicon brandId={brand.brandId} size={20} />
              <ListItemText
                primary={brand.name}
                secondary={brand.url}
                primaryTypographyProps={{ fontSize: '0.85rem', fontWeight: 500 }}
                secondaryTypographyProps={{ fontSize: '0.75rem', noWrap: true }}
              />
              {brand.brandId === activeBrand.brandId && (
                <ListItemIcon sx={{ minWidth: 'auto' }}>
                  <CheckIcon sx={{ fontSize: 18, color: 'primary.main' }} />
                </ListItemIcon>
              )}
            </ListItemButton>
          ))}
          {filtered.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ px: 2, py: 1 }}>
              {t('brandSelector.noBrandFound')}
            </Typography>
          )}
        </List>

        <Divider />
        <ListItemButton
          onClick={() => { navigate('/settings?tab=brands'); setAnchorEl(null); }}
          sx={{ py: 1 }}
        >
          <ListItemIcon sx={{ minWidth: 32 }}>
            <SettingsIcon sx={{ fontSize: 18 }} />
          </ListItemIcon>
          <ListItemText
            primary={t('brandSelector.manageBrands')}
            primaryTypographyProps={{ fontSize: '0.8rem', color: 'text.secondary' }}
          />
        </ListItemButton>
      </Popover>
    </>
  );
}
